import type { TPlanOptions } from '@constant/index'

const SESSION_STORAGE_KEY = 'invoice-onboarding-analytics-v2-session'
const LEGACY_LOCAL_STORAGE_KEY = 'invoice-onboarding-analytics-v2-session'
/** Align analytics session lifetime with the guide hide duration. */
const SESSION_TTL_MS = 24 * 60 * 60 * 1000

export type OnboardingAnalyticsTimestampKey =
  | 'started'
  | 'goals_submitted'
  | 'pricing_viewed'
  | 'plan_selected'
  | 'billing_redirect_opened'
  | 'billing_completed'
  | 'completed'

export type OnboardingAnalyticsMilestone =
  | 'started'
  | 'goals_submitted'
  | 'recommendation_generated'
  | 'pricing_viewed'
  | 'billing_redirect_opened'
  | 'billing_completed'
  | 'completed'
  | `step_viewed:${string}`
  | `step_completed:${string}`

export type RecommendationSource = 'contentful' | 'fallback' | 'empty_goals_default'

export type OnboardingAnalyticsSession = {
  sessionId: string
  /** Scopes milestones to the current shop so switching stores does not reuse dedupe state. */
  shopId: string
  startedAt: string
  ended: boolean
  milestones: Partial<Record<OnboardingAnalyticsMilestone, true>>
  timestamps: Partial<Record<OnboardingAnalyticsTimestampKey, string>>
  purchasedPlan: TPlanOptions | null
  goalsSource: RecommendationSource | null
}

export const getAnalyticsShopId = (): string => {
  if (typeof window === 'undefined') return ''
  try {
    const raw = window.localStorage.getItem('whoami')
    if (!raw) return ''
    const parsed = JSON.parse(raw) as { shop_id?: string | number }
    return parsed.shop_id != null ? String(parsed.shop_id) : ''
  } catch {
    return ''
  }
}

const createEmptySession = (
  sessionId: string,
  shopId: string,
): OnboardingAnalyticsSession => ({
  sessionId,
  shopId,
  startedAt: new Date().toISOString(),
  ended: false,
  milestones: {},
  timestamps: {},
  purchasedPlan: null,
  goalsSource: null,
})

const safeParse = (value: string | null): OnboardingAnalyticsSession | null => {
  if (!value) return null
  try {
    return JSON.parse(value) as OnboardingAnalyticsSession
  } catch {
    return null
  }
}

const readStorageItem = (key: string): string | null => {
  if (typeof window === 'undefined') return null
  return window.sessionStorage.getItem(key)
}

const writeStorageItem = (key: string, value: string) => {
  if (typeof window === 'undefined') return
  window.sessionStorage.setItem(key, value)
}

const removeStorageItem = (key: string) => {
  if (typeof window === 'undefined') return
  window.sessionStorage.removeItem(key)
}

const migrateLegacyLocalStorageSession = () => {
  if (typeof window === 'undefined') return
  const legacy = window.localStorage.getItem(LEGACY_LOCAL_STORAGE_KEY)
  if (!legacy || readStorageItem(SESSION_STORAGE_KEY)) return
  writeStorageItem(SESSION_STORAGE_KEY, legacy)
  window.localStorage.removeItem(LEGACY_LOCAL_STORAGE_KEY)
}

const isSessionExpired = (session: OnboardingAnalyticsSession): boolean => {
  const started = Date.parse(session.startedAt)
  if (Number.isNaN(started)) return true
  return Date.now() - started > SESSION_TTL_MS
}

const isSessionForCurrentShop = (session: OnboardingAnalyticsSession): boolean => {
  const shopId = getAnalyticsShopId()
  if (!shopId) return true
  if (!session.shopId) return false
  return session.shopId === shopId
}

export const readOnboardingAnalyticsSession = (): OnboardingAnalyticsSession | null => {
  migrateLegacyLocalStorageSession()
  const session = safeParse(readStorageItem(SESSION_STORAGE_KEY))
  if (!session || session.ended || isSessionExpired(session)) return null
  if (!isSessionForCurrentShop(session)) {
    removeStorageItem(SESSION_STORAGE_KEY)
    return null
  }
  return session
}

const writeOnboardingAnalyticsSession = (session: OnboardingAnalyticsSession) => {
  writeStorageItem(SESSION_STORAGE_KEY, JSON.stringify(session))
}

export const ensureOnboardingAnalyticsSession = (): OnboardingAnalyticsSession => {
  const existing = readOnboardingAnalyticsSession()
  if (existing) return existing

  const session = createEmptySession(crypto.randomUUID(), getAnalyticsShopId())
  writeOnboardingAnalyticsSession(session)
  return session
}

export const endOnboardingAnalyticsSession = () => {
  removeStorageItem(SESSION_STORAGE_KEY)
}

export const setOnboardingGoalsSource = (source: RecommendationSource) => {
  const session = ensureOnboardingAnalyticsSession()
  writeOnboardingAnalyticsSession({ ...session, goalsSource: source })
}

export const setOnboardingPurchasedPlan = (plan: TPlanOptions) => {
  const session = ensureOnboardingAnalyticsSession()
  writeOnboardingAnalyticsSession({ ...session, purchasedPlan: plan })
}

export const hasOnboardingMilestoneFired = (
  milestone: OnboardingAnalyticsMilestone,
): boolean => {
  const session = readOnboardingAnalyticsSession()
  return Boolean(session?.milestones[milestone])
}

export const markOnboardingMilestoneFired = (
  milestone: OnboardingAnalyticsMilestone,
): boolean => {
  const session = ensureOnboardingAnalyticsSession()
  if (session.milestones[milestone]) return false

  const next: OnboardingAnalyticsSession = {
    ...session,
    milestones: { ...session.milestones, [milestone]: true },
  }
  writeOnboardingAnalyticsSession(next)
  return true
}

export const recordOnboardingTimestamp = (key: OnboardingAnalyticsTimestampKey) => {
  const session = ensureOnboardingAnalyticsSession()
  if (session.timestamps[key]) return

  writeOnboardingAnalyticsSession({
    ...session,
    timestamps: { ...session.timestamps, [key]: new Date().toISOString() },
  })
}

const elapsedMs = (fromIso: string | undefined, toIso?: string): number | null => {
  if (!fromIso) return null
  const from = Date.parse(fromIso)
  if (Number.isNaN(from)) return null
  const to = toIso ? Date.parse(toIso) : Date.now()
  if (Number.isNaN(to)) return null
  return Math.max(0, to - from)
}

export const getOnboardingElapsedMetrics = (
  session: OnboardingAnalyticsSession | null = readOnboardingAnalyticsSession(),
  nowIso = new Date().toISOString(),
): Record<string, number | null> => {
  if (!session) {
    return {
      ms_since_started: null,
      ms_started_to_goals_submitted: null,
      ms_started_to_pricing_viewed: null,
      ms_pricing_viewed_to_plan_selected: null,
      ms_billing_opened_to_billing_completed: null,
      ms_started_to_completed: null,
    }
  }

  const { timestamps, startedAt } = session

  return {
    ms_since_started: elapsedMs(startedAt, nowIso),
    ms_started_to_goals_submitted: elapsedMs(startedAt, timestamps.goals_submitted),
    ms_started_to_pricing_viewed: elapsedMs(startedAt, timestamps.pricing_viewed),
    ms_pricing_viewed_to_plan_selected: elapsedMs(
      timestamps.pricing_viewed,
      timestamps.plan_selected,
    ),
    ms_billing_opened_to_billing_completed: elapsedMs(
      timestamps.billing_redirect_opened,
      timestamps.billing_completed,
    ),
    ms_started_to_completed: elapsedMs(startedAt, timestamps.completed),
  }
}
